import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import Suggestions from './Suggestions';
import { sendMessageWithSession } from '../services/sessionManager';
import logoImage from '../assets/logo.png';

/**
 * Main container component for the chat widget.
 *
 * @param {Object} props - Component props
 * @param {Object} props.config - Widget configuration
 * @param {Function} props.onClose - Callback when widget is closed
 * @returns {JSX.Element} Container component
 */
const Container = ({ config, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [activeTimeId, setActiveTimeId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const apiErrorCount = useRef(0);

  // Configuration options with defaults
  const {
    tenant = 'default',
    apiUrl = null, // Will use configured URL if not provided
    chatbotName = 'Trung Tâm',
    welcomeMessage = 'Xin chào! Tôi có thể giúp gì cho bạn?',
    logo = logoImage,
    placeholder = 'Nhập câu hỏi của bạn...',
    buttonText = 'Gửi',
    initialSuggestions = [],
    useDebugProxy = false
  } = config || {};

  // Hàm gọi API, có hỗ trợ proxy cho debug
  const callAPI = async (endpoint, method, data) => {
    // Sử dụng API URL từ config, KHÔNG tự tạo URL mới
    let url = endpoint.startsWith('/')
      ? `${apiUrl}${endpoint}`
      : `${apiUrl}/${endpoint}`;

    if (useDebugProxy && window.TALKFLOW_PROXY_URL) {
      url = `${window.TALKFLOW_PROXY_URL}/${url}`;
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: data ? JSON.stringify(data) : undefined
      });

      if (!response.ok) {
        // Cố gắng đọc nội dung lỗi
        const errorText = await response.text();
        console.error(`API error (${response.status}):`, errorText);

        // Kiểm tra nếu là lỗi validation từ Pydantic
        if (errorText.includes('validation errors') && errorText.includes('sources')) {
          console.warn('Detected sources validation error, using fallback response');

          // Nếu là endpoint chat, tạo phản hồi giả lập
          if (endpoint === '/api/v1/chat/chat' && method === 'POST') {
            // Trích xuất nội dung hữu ích từ lỗi nếu có thể
            const usefulContent = extractContentFromError(errorText);

            // Nếu có nội dung hữu ích, sử dụng nó
            if (usefulContent) {
              console.log('Successfully extracted content from error:', usefulContent.substring(0, 100) + '...');

              return {
                response: usefulContent,
                tenant_id: data?.tenant_id || tenant,
                sources: null,
                suggestions: [
                  "Bạn cần thêm thông tin về vấn đề này không?",
                  "Tôi có thể giúp gì thêm cho bạn?",
                  "Liên hệ hỗ trợ"
                ]
              };
            } else {
              // Nếu không trích xuất được nội dung, sử dụng phản hồi mặc định
              return {
                response: "Hiện tại tôi không thể kết nối với nguồn dữ liệu. Vui lòng thử lại sau hoặc liên hệ hỗ trợ kỹ thuật nếu vấn đề vẫn tiếp tục.",
                tenant_id: data?.tenant_id || tenant,
                sources: null,
                suggestions: [
                  "Thử lại với câu hỏi khác",
                  "Kiểm tra kết nối API",
                  "Liên hệ hỗ trợ"
                ]
              };
            }
          }
        }

        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error calling ${endpoint}:`, error);
      throw error;
    }
  };

  // Hàm trích xuất nội dung hữu ích từ lỗi validation
  const extractContentFromError = (errorText) => {
    try {
      // Trước tiên, tìm toàn bộ các phần input_value
      const matches = errorText.match(/input_value='([^']+)'/g);
      if (!matches || matches.length === 0) return null;

      // Kết hợp tất cả các phần trích xuất từ lỗi
      let allContent = '';

      // Xử lý từng phần input_value
      for (const match of matches) {
        // Lấy nội dung bên trong dấu nháy đơn
        let content = match.replace(/input_value='/, '').slice(0, -1);

        // Nếu bắt đầu bằng 'Q:', đây là một câu hỏi từ FAQ
        if (content.startsWith('Q:')) {
          // Tách câu hỏi và câu trả lời
          const parts = content.split('\n');
          if (parts.length > 1) {
            // Bỏ qua phần câu hỏi, chỉ lấy câu trả lời
            content = parts.slice(1).join('\n').trim();
          } else {
            content = content.substring(2).trim();
          }
        }

        // Thêm vào nội dung tổng hợp
        if (content && !allContent.includes(content)) {
          allContent += (allContent ? '\n\n' : '') + content;
        }
      }

      // Nếu có nội dung hữu ích, trả về
      return allContent || null;
    } catch (e) {
      console.error('Error extracting content from error:', e);
      return null;
    }
  };

  // Improved scrolling implementation
  useEffect(() => {
    if (messages.length === 0) return;

    // Use a small delay to ensure the DOM has updated
    const scrollTimeout = setTimeout(() => {
      try {
        // Get the message container
        const messagesContainer = document.querySelector('.talkflow-messages');
        if (!messagesContainer) return;

        // Get the last message element
        const messageElements = document.querySelectorAll('.talkflow-messages .talkflow-chat-bubble');
        if (!messageElements.length) return;

        const lastMessage = messageElements[messageElements.length - 1];
        if (!lastMessage) return;

        // Calculate optimal scroll position
        // This positions the latest message at the top with minimal padding
        const containerRect = messagesContainer.getBoundingClientRect();
        const messageRect = lastMessage.getBoundingClientRect();
        const topOffset = messageRect.top - containerRect.top;

        // Add the current scroll position to the relative offset
        const scrollPosition = messagesContainer.scrollTop + topOffset - 20;

        // Perform the scroll
        messagesContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      } catch (error) {
        console.error('Error during scrolling:', error);
        // Fallback to basic scrolling if anything fails
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 150); // Slightly longer delay to ensure rendering is complete

    // Clean up timeout on unmount
    return () => clearTimeout(scrollTimeout);
  }, [messages]);

  // Add welcome message on first load - chỉ chạy một lần khi component được mount
  useEffect(() => {
    if (welcomeMessage) {
      const welcomeMsg = isOfflineMode
        ? `${welcomeMessage} (Chế độ ngoại tuyến: API không khả dụng)`
        : welcomeMessage;

      setMessages([
        {
          type: 'assistant',
          content: welcomeMsg,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }

    if (initialSuggestions && initialSuggestions.length > 0) {
      setSuggestions(initialSuggestions);
    }
  }, [welcomeMessage, initialSuggestions, isOfflineMode]);

  // Lưu giá trị config vào ref để tránh render lại
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Hàm mô phỏng phản hồi khi ở chế độ ngoại tuyến với hỗ trợ sức khỏe tâm thần
  const getOfflineResponse = (message) => {
    const messageLower = message.toLowerCase();

    // Mental health focused offline responses
    const mentalHealthResponses = {
      "xin chào": "Xin chào! Tôi là trợ lý AI. Tôi có thể giúp gì cho bạn?",
      "hello": "Hello! Tôi có thể hỗ trợ bạn. Bạn cần hỗ trợ gì?",
      "giúp": "Tôi có thể cung cấp thông tin hỗ trợ. Bạn muốn hỏi về vấn đề gì?",
      "help": "Tôi có thể hỗ trợ bạn. Tuy nhiên, cho các vấn đề nghiêm trọng, hãy tìm chuyên gia."
    };

    // Check for mental health keywords
    for (const [keyword, response] of Object.entries(mentalHealthResponses)) {
      if (messageLower.includes(keyword)) {
        return response;
      }
    }

    // Default response for unrecognized messages
    return `**🌐 Chế độ ngoại tuyến**

Hiện tại tôi không thể kết nối với hệ thống để trả lời câu hỏi của bạn một cách đầy đủ. 

**📞 Hỗ trợ khẩn cấp**
• **Đường dây nóng tâm lý**: 1800-1337 (miễn phí 24/7)
• **Cấp cứu**: 115
• **Tư vấn tâm lý**: 19001567

**💡 Lời khuyên tức thì**
• Hãy thử thở chậm và sâu để bình tĩnh lại
• Liên hệ với người thân tin cậy
• Ghi lại những cảm xúc hiện tại

**🔄 Thử lại sau**
Câu hỏi "*${message.length > 50 ? message.substring(0, 50) + '...' : message}*" sẽ được trả lời chi tiết hơn khi hệ thống hoạt động trở lại.`;
  };

  const handleSendMessage = async (message) => {
    // Add user message to chat
    const userMessage = {
      type: 'user',
      content: message,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    setIsLoading(true);

    // Nếu đang ở chế độ ngoại tuyến, sử dụng phản hồi giả lập
    if (isOfflineMode) {
      setTimeout(() => {
        const offlineResponse = getOfflineResponse(message);
        const assistantMessage = {
          type: 'assistant',
          content: offlineResponse,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prevMessages => [...prevMessages, assistantMessage]);
        setSuggestions([
          'Kiểm tra kết nối API',
          'Làm thế nào để cấu hình?',
          'Giúp tôi giải quyết vấn đề'
        ]);
        setIsLoading(false);
      }, 1000);
      return;
    }

    try {
      // Prepare message history
      const history = messages.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      console.log('Sending message to API:', message);

      // Sử dụng sendMessageWithSession để tái sử dụng session
      const data = await sendMessageWithSession(message, tenant, history);

      console.log('API response:', data);

      // Process response and check for "no information found" case
      let messageType = 'assistant';
      let responseContent = data.response || "Hiện tại tôi không thể xử lý yêu cầu này. Vui lòng thử lại sau.";

      // Check if this is a "no information found" response based on hasContext or method
      const isNoInfoFound = data.hasContext === false || data.method === 'no-context-found' ||
        responseContent.includes('không tìm thấy thông tin phù hợp');

      if (isNoInfoFound) {
        messageType = 'system'; // Use system type for special styling
      }

      console.log('🔧 DEBUG API Response:', {
        separateReferences: data.separateReferences,
        separateAttachments: data.separateAttachments,
        hasContext: data.hasContext,
        separateReferencesType: typeof data.separateReferences,
        separateReferencesLength: data.separateReferences ? data.separateReferences.length : 0
      });

      const assistantMessage = {
        type: messageType,
        content: responseContent,
        separateReferences: data.separateReferences || '',
        separateAttachments: data.separateAttachments || '',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        // Thêm thông tin model để debug (có thể hiển thị trong dev mode)
        modelInfo: data.model_info,
        confidence: data.confidence,
        hasContext: data.hasContext
      };

      console.log('🔧 DEBUG Assistant Message:', {
        separateReferences: assistantMessage.separateReferences,
        separateAttachments: assistantMessage.separateAttachments,
        separateReferencesType: typeof assistantMessage.separateReferences,
        separateReferencesLength: assistantMessage.separateReferences ? assistantMessage.separateReferences.length : 0
      });

      console.log('🔧 DEBUG Message Object:', assistantMessage);

      // Remove typing indicator and add actual response
      setMessages(prevMessages => {
        const filtered = prevMessages.filter(msg => !msg.isTyping);
        return [...filtered, assistantMessage];
      });

      // Handle suggestions from API response
      if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length > 0) {
        console.log('Received suggestions from API:', data.suggestions);
        setSuggestions(data.suggestions);
      } else {
        // Clear suggestions if none provided
        setSuggestions([]);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error sending message:', error);

      // Hiển thị chi tiết lỗi để debug
      let errorDetails = error.message;
      console.error('Error details:', errorDetails);

      // Chuyển sang sử dụng phản hồi ngoại tuyến nếu API không khả dụng
      const offlineResponse = getOfflineResponse(message);

      // Create comprehensive error message based on error type
      let errorIcon = '⚠️';
      let errorTitle = 'Lỗi kết nối';
      let errorSolution = 'Vui lòng thử lại sau';

      if (errorDetails.includes('timeout')) {
        errorIcon = '⏱️';
        errorTitle = 'Hết thời gian chờ';
        errorSolution = 'Hệ thống đang xử lý chậm, hãy thử lại';
      } else if (errorDetails.includes('404')) {
        errorIcon = '🔍';
        errorTitle = 'Dịch vụ không khả dụng';
        errorSolution = 'API endpoint không tồn tại, vui lòng kiểm tra cấu hình';
      } else if (errorDetails.includes('500')) {
        errorIcon = '🔧';
        errorTitle = 'Lỗi máy chủ';
        errorSolution = 'Máy chủ đang gặp sự cố, vui lòng thử lại sau';
      } else if (errorDetails.includes('network') || errorDetails.includes('fetch')) {
        errorIcon = '🌐';
        errorTitle = 'Lỗi mạng';
        errorSolution = 'Kiểm tra kết nối internet và thử lại';
      } else if (errorDetails.includes('CORS')) {
        errorIcon = '🔒';
        errorTitle = 'Lỗi bảo mật CORS';
        errorSolution = 'Cấu hình CORS không đúng, vui lòng liên hệ quản trị viên';
      }

      const errorMessage = {
        type: 'system',
        content: `${errorIcon} **${errorTitle}**

${errorSolution}

**💡 Trong lúc chờ đợi, đây là một số lời khuyên:**

${offlineResponse}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      // Remove typing indicator and add error message
      setMessages(prevMessages => {
        const filtered = prevMessages.filter(msg => !msg.isTyping);
        return [...filtered, errorMessage];
      });
      setSuggestions([
        'Thử lại với câu hỏi khác',
        'Kiểm tra kết nối API',
        'Liên hệ hỗ trợ'
      ]);
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  // Handle expand/collapse toggle
  const handleExpandToggle = () => {
    if (isExpanded) {
      // Thu nhỏ với fade out effect
      const widget = document.querySelector('.talkflow-widget');
      if (widget) {
        widget.classList.add('talkflow-collapsing');

        // Đợi animation hoàn thành rồi mới remove expanded class
        setTimeout(() => {
          setIsExpanded(false);
          widget.classList.remove('talkflow-collapsing');
        }, 180);
      } else {
        setIsExpanded(false);
      }
    } else {
      // Mở rộng
      setIsExpanded(true);
    }
  };

  // Update useEffect to handle clicks and keyboard shortcuts
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if the click is outside the chat bubbles
      if (!e.target.closest('.talkflow-chat-bubble')) {
        setActiveTimeId(null);
      }
    };

    const handleKeyDown = (e) => {
      // ESC key to exit fullscreen
      if (e.key === 'Escape' && isExpanded) {
        handleExpandToggle();
      }
    };

    // Add the click listener to the widget instead of document
    const widgetElement = document.querySelector('.talkflow-widget');
    if (widgetElement) {
      widgetElement.addEventListener('click', handleClickOutside);
    }

    // Add keyboard listener for ESC key
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (widgetElement) {
        widgetElement.removeEventListener('click', handleClickOutside);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);

  // Manual health check function for refresh button only
  const manualHealthCheck = async () => {
    try {
      console.log('Manual health check...');
      await callAPI('/api/v1/health', 'GET');
      setIsOfflineMode(false);
    } catch (error) {
      console.error('Manual health check failed:', error);
      setIsOfflineMode(true);
    }
  };

  return (
    <div className={`talkflow-widget ${isExpanded ? 'talkflow-expanded' : ''}`}>
      <Header
        title={chatbotName + (isOfflineMode ? " (Ngoại tuyến)" : "")}
        logo={logo}
        onClose={onClose}
        onExpand={handleExpandToggle}
        isExpanded={isExpanded}
      />

      <div className="talkflow-messages">
        {Array.isArray(messages) ? messages.map((message, index) => {
          console.log(`🔧 DEBUG Rendering message ${index}:`, {
            type: message.type,
            isUser: message.type === 'user',
            separateReferences: message.separateReferences,
            separateAttachments: message.separateAttachments
          });

          return (
            <ChatBubble
              key={index}
              messageId={index}
              type={message.type}
              message={message.content}
              isUser={message.type === 'user'}
              separateReferences={message.separateReferences || ''}
              separateAttachments={message.separateAttachments || ''}
              time={message.time}
              activeTimeId={activeTimeId}
              setActiveTimeId={setActiveTimeId}
              isNoInfoFound={message.type === 'system' &&
                (message.content.includes('không tìm thấy thông tin phù hợp') ||
                  message.hasContext === false)}
              isTyping={message.isTyping || false}
              confidence={message.confidence || null}
            />
          );
        }) : null}

        {isLoading && (
          <div className="talkflow-loading">
            <div className="talkflow-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {!isLoading && Array.isArray(suggestions) && suggestions.length > 0 && (
          <Suggestions
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            disabled={isLoading}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="talkflow-footer">
        <div className="talkflow-input-section">
          <ChatInput
            onSendMessage={handleSendMessage}
            placeholder={placeholder}
            buttonText={buttonText}
          />
        </div>

        {isOfflineMode && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '8px',
            fontSize: '12px',
            textAlign: 'center',
            borderTop: '1px solid #ffeeba'
          }}>
            Đang hoạt động ở chế độ ngoại tuyến. API không khả dụng.
            <button
              onClick={manualHealthCheck}
              style={{
                marginLeft: '8px',
                background: '#ffc107',
                border: 'none',
                padding: '2px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px'
              }}
            >
              Kiểm tra lại
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Container;
